<template>
  <section
    class="guided-recorder"
    :aria-labelledby="!previewOnly && labelledBy ? labelledBy : undefined"
    :aria-label="previewOnly || !labelledBy ? $t('routes.guidedRecording.previewMode') : undefined"
    data-cy="guidedRecording-recorder"
  >
    <v-alert v-if="capabilityState === 'unsupported'" type="error" variant="tonal" role="alert" data-cy="guidedRecording-recorder-unsupported">
      {{ $t('routes.guidedRecording.browserUnsupported') }}
    </v-alert>
    <v-alert v-else-if="capabilityState === 'mimeUnsupported'" type="error" variant="tonal" role="alert">
      {{ $t('routes.guidedRecording.mimeUnsupported') }}
    </v-alert>
    <template v-else-if="capabilityState === 'supported'">
      <v-alert
        v-if="errorKey || sourceErrorKey" ref="errorFeedback" type="error" variant="tonal"
        class="guided-recorder__error mb-2" role="alert" aria-live="assertive" aria-atomic="true"
        tabindex="-1" data-cy="guidedRecording-recorder-error"
      >
        {{ $t(`routes.guidedRecording.${errorKey || sourceErrorKey}`) }}
      </v-alert>
      <v-alert
        v-if="!previewOnly && !hasValidMaximumDuration" ref="durationPolicyFeedback"
        type="error" variant="tonal" class="mb-2" role="alert" tabindex="-1"
        data-cy="guidedRecording-recorder-durationPolicy"
      >
        {{ $t('routes.guidedRecording.durationPolicyUnavailable') }}
      </v-alert>
      <div class="guided-recorder__preview mb-2" :style="{ aspectRatio: previewAspectRatio, '--guided-source-ratio': previewRatio }">
        <video
          ref="livePreview" class="guided-recorder__video" muted playsinline
          :aria-label="$t('routes.guidedRecording.livePreview')"
          data-cy="guidedRecording-recorder-livePreview"
          @loadedmetadata="updatePreviewDimensions" @resize="updatePreviewDimensions"
        />
        <div v-if="!stream" class="guided-recorder__preview-placeholder" aria-hidden="true">
          <v-icon size="48">mdi-video-off-outline</v-icon>
        </div>
        <!-- Coaching and countdown are DOM-only, never painted into the recording canvas. -->
        <div
          v-if="stream && previewPlaneStyle && (framingState === 'tracking' || framingState === 'missing')"
          class="guided-recorder__framing-plane" :style="previewPlaneStyle" aria-hidden="true"
          data-cy="guidedRecording-recorder-framing"
        >
          <div class="guided-recorder__framing-target" :style="framingTargetStyle">
            <span>{{ $t('routes.guidedRecording.framingTarget') }}</span>
          </div>
          <div
            v-if="faceBounds" class="guided-recorder__face-box"
            :class="{ 'guided-recorder__face-box--good': !framingCorrection }"
            :style="faceBoxStyle" data-cy="guidedRecording-recorder-faceBox"
          />
          <span
            v-for="(point, index) in faceLandmarks" :key="index"
            class="guided-recorder__face-point" :style="{ left: `${point.x * 100}%`, top: `${point.y * 100}%` }"
          />
        </div>
        <div
          v-if="stream" class="guided-recorder__framing-cue" role="status" aria-live="polite" aria-atomic="true"
          data-cy="guidedRecording-recorder-framingCue"
        >
          <v-icon aria-hidden="true">{{ framingIcon }}</v-icon>
          {{ $t(`routes.guidedRecording.${framingCorrection || framingStatusKey}`) }}
        </div>
        <div v-if="isRecording" class="guided-recorder__recording-indicator" data-cy="guidedRecording-recorder-recordingIndicator">
          <v-icon aria-hidden="true">mdi-record-circle</v-icon>
          {{ $t('routes.guidedRecording.recording') }}
        </div>
        <div
          v-if="countdown" class="guided-recorder__countdown" role="status" aria-live="polite" aria-atomic="true"
          :aria-label="$t('routes.guidedRecording.countdown', { count: countdown })"
          data-cy="guidedRecording-recorder-countdown"
        ><span aria-hidden="true">{{ countdown }}</span></div>
      </div>
      <div class="guided-recorder__metrics mb-2">
        <section
          v-if="!previewOnly && hasValidMaximumDuration" class="guided-recorder__duration"
          :aria-label="$t('routes.guidedRecording.recordingDuration')" data-cy="guidedRecording-recorder-duration"
        >
          <div class="guided-recorder__duration-values">
            <span>{{ $t('routes.guidedRecording.recordingMaximumDuration', { duration: formatDuration(maximumDurationSeconds) }) }}</span>
            <span data-cy="guidedRecording-recorder-elapsed">{{ $t('routes.guidedRecording.recordingElapsedTime', { duration: formatDuration(displayElapsedSeconds) }) }}</span>
            <span data-cy="guidedRecording-recorder-remaining">{{ $t('routes.guidedRecording.recordingRemainingTime', { duration: formatDuration(displayRemainingSeconds, true) }) }}</span>
          </div>
          <v-progress-linear
            :model-value="durationProgress" color="primary" height="6" rounded role="progressbar"
            bg-color="tw-surface-accent" :bg-opacity="1" :opacity="1"
            :aria-label="$t('routes.guidedRecording.recordingDuration')" aria-valuemin="0" aria-valuemax="100"
            :aria-valuenow="durationProgress" :aria-valuetext="durationProgressAriaText"
            data-cy="guidedRecording-recorder-durationProgress"
          />
        </section>
        <div v-else />
        <div class="guided-recorder__audio" role="group" :aria-label="$t('routes.guidedRecording.audioLevel')">
          <v-icon aria-hidden="true">mdi-microphone-outline</v-icon>
          <div
            class="guided-recorder__audio-meter" role="meter"
            :aria-label="$t('routes.guidedRecording.audioLevel')" aria-valuemin="0" aria-valuemax="100"
            :aria-valuenow="audioLevel"
            :aria-valuetext="$t('routes.guidedRecording.audioLevelValue', { level: audioLevel })"
          ><span :style="{ width: `${audioLevel}%` }" /></div>
        </div>
      </div>
      <v-alert
        v-for="warning in capabilityWarnings" :key="warning" type="warning" variant="tonal"
        class="mb-2" role="status" aria-live="polite" aria-atomic="true"
        data-cy="guidedRecording-recorder-capabilityWarning"
      >{{ $t(`routes.guidedRecording.${warning}`) }}</v-alert>
      <v-btn
        v-if="stream && audioHint === 'microphoneAnalysisUnavailable'" variant="outlined" class="mb-2"
        data-cy="guidedRecording-recorder-resumeAudioAnalysis" @click="resumeAudioAnalysis"
      >{{ $t('routes.guidedRecording.resumeMicrophoneAnalysis') }}</v-btn>
      <div class="guided-recorder__autocue"><slot name="below-preview" /></div>
      <v-alert
        v-if="durationWarningAnnounced && isRecording" type="warning" variant="tonal"
        class="mb-2" role="status" aria-live="assertive" aria-atomic="true"
        data-cy="guidedRecording-recorder-durationWarning"
      >{{ durationWarningMessage }}</v-alert>
      <div class="guided-recorder__actions">
        <v-btn
          v-if="!stream" color="primary" variant="outlined" :loading="sourceRequesting"
          :disabled="isBusy" data-cy="guidedRecording-recorder-startPreview" @click="openPreview"
        >
          <v-icon start aria-hidden="true">mdi-video-plus-outline</v-icon>{{ $t('routes.guidedRecording.startPreview') }}
        </v-btn>
        <v-btn
          v-else variant="outlined" :disabled="isBusy || isRecording"
          data-cy="guidedRecording-recorder-stopPreview" @click="releaseStream"
        >
          <v-icon start aria-hidden="true">mdi-video-off-outline</v-icon>{{ $t('routes.guidedRecording.stopPreview') }}
        </v-btn>
        <v-btn v-if="isRecording" color="error" data-cy="guidedRecording-recorder-stop" @click="stopRecording()">
          <v-icon start aria-hidden="true">mdi-stop-circle-outline</v-icon>{{ $t('routes.guidedRecording.stopRecording') }}
        </v-btn>
        <v-btn
          v-else-if="!previewOnly" color="primary"
          :disabled="!stream || isBusy || !hasValidMaximumDuration || blurState === 'warming'"
          data-cy="guidedRecording-recorder-record" @click="startCountdown"
        >
          <v-icon start aria-hidden="true">mdi-record-circle-outline</v-icon>{{ $t('routes.guidedRecording.record') }}
        </v-btn>
      </div>
    </template>
  </section>
</template>

<script>
import { markRaw } from 'vue';
import { createGuidedAudioAnalysis } from '@/routes/guided-recording/media/GuidedAudioAnalysis';
import { createGuidedVideoPipeline } from '@/routes/guided-recording/media/GuidedVideoPipeline';
import {
  getGuidedFramingCorrection,
  getGuidedFramingTargetBounds,
  getGuidedPreviewRect,
} from '@/routes/guided-recording/media/GuidedVisualGuidance';

const MIME_TYPES = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
];

const GUIDED_RECORDING_MINIMUM_DURATION_SECONDS = 5;
const GUIDED_RECORDING_MAXIMUM_DURATION_SECONDS = 600;
const GUIDED_RECORDING_FINAL_WARNING_SECONDS = 10;
const GUIDED_RECORDING_AUTOMATIC_STOP_TOLERANCE_SECONDS = 1;
const RECORDING_TIMER_INTERVAL_MS = 250;
const CAPTURE_PREROLL_SECONDS = 5;
const DURATION_INVALID_REASON = 'durationExceededMaximum';
const DURATION_TOLERANCE_INVALID_REASON = 'durationExceededAutomaticStopTolerance';

const isValidEffectiveDuration = (durationSeconds) => (
  Number.isInteger(durationSeconds)
  && durationSeconds >= GUIDED_RECORDING_MINIMUM_DURATION_SECONDS
  && durationSeconds <= GUIDED_RECORDING_MAXIMUM_DURATION_SECONDS
);

const readMonotonicTime = () => {
  const clockValue = typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();

  return Number.isFinite(clockValue) ? clockValue : 0;
};

const isMeasuredDurationWithinLimit = (durationSeconds, maximumDurationSeconds, automaticStop) => {
  if (!isValidEffectiveDuration(maximumDurationSeconds)
    || !Number.isFinite(durationSeconds)
    || durationSeconds < 0
  ) {
    return false;
  }

  const tolerance = automaticStop ? GUIDED_RECORDING_AUTOMATIC_STOP_TOLERANCE_SECONDS : 0;
  return durationSeconds <= maximumDurationSeconds + tolerance;
};


const isRecorderSupported = () => Boolean(window.MediaRecorder && window.MediaStream);

const getSupportedMimeType = () => (
  typeof window.MediaRecorder?.isTypeSupported === 'function'
    ? MIME_TYPES.find((type) => window.MediaRecorder.isTypeSupported(type)) || ''
    : ''
);


export default {
  name: 'GuidedRecorder',
  emits: ['recorded', 'timer-tick', 'is-recording', 'busy-change', 'request-preview', 'release-source', 'capability-change'],
  props: {
    maximumDurationSeconds: {
      type: Number,
      default: undefined,
      validator: value => value === undefined || isValidEffectiveDuration(value),
    },
    sourceStream: { type: Object, default: null },
    previewOnly: { type: Boolean, default: false },
    sourceRequesting: { type: Boolean, default: false },
    sourceErrorKey: { type: String, default: '' },
    blurEnabled: { type: Boolean, default: false },
    labelledBy: { type: String, default: '' },
  },
  data() {
    return {
      runtime: markRaw({ pipeline: null, analysis: null, rawVideo: null, tracks: [], previewObserver: null }),
      audioLevel: 0,
      audioHint: null,
      faceBounds: null,
      faceLandmarks: [],
      framingCorrection: null,
      framingState: 'loading',
      hasTrackedFace: false,
      visionState: 'loading',
      visionReason: null,
      blurReason: null,
      previewPlaneStyle: null,
      blurState: 'off',
      countdown: 0,
      countdownTimer: null,
      durationWarningAnnounced: false,
      durationWarningSeconds: null,
      capabilityState: 'unresolved',
      errorKey: '',
      disposed: false,
      isRecording: false,
      mediaRecorder: null,
      mimeType: '',
      recordedChunks: [],
      recordingAutomaticStop: false,
      recordingDurationInvalidReason: '',
      recordingDurationValid: false,
      recordingElapsedSeconds: 0,
      recordingInvalidated: false,
      recordingRemainingSeconds: 0,
      recordingStartedAt: null,
      recordingStopRequested: false,
      recordingUsesCanvas: false,
      recordingHiddenStop: false,
      recordingStoppedAt: null,
      recordingTimer: null,
      stream: null,
      previewAspectRatio: '16 / 9',
      previewRatio: 16 / 9,
      previewRequestVersion: 0,
      lastMonotonicTime: 0,
    };
  },
  computed: {
    framingTargetStyle() {
      return this.normalizedBoundsStyle(getGuidedFramingTargetBounds(this.previewRatio));
    },
    faceBoxStyle() {
      return this.faceBounds ? this.normalizedBoundsStyle(this.faceBounds) : null;
    },
    framingStatusKey() {
      if (this.framingState === 'unavailable') return 'framingUnavailable';
      if (this.framingState === 'loading') return 'framingLoading';
      if (this.framingState === 'missing') return this.hasTrackedFace ? 'framingFaceLost' : 'framingLookingForFace';
      return this.framingCorrection ? 'framingAdjustPosition' : 'framingGoodPosition';
    },
    framingIcon() {
      const icons = {
        faceMoveLeft: 'mdi-arrow-left',
        faceMoveRight: 'mdi-arrow-right',
        faceMoveUp: 'mdi-arrow-up',
        faceMoveDown: 'mdi-arrow-down',
        faceMoveCloser: 'mdi-arrow-collapse',
        faceMoveFarther: 'mdi-arrow-expand',
      };
      if (this.framingCorrection) return icons[this.framingCorrection];
      if (this.framingState === 'tracking') return 'mdi-check-circle-outline';
      return this.framingState === 'unavailable' ? 'mdi-camera-off-outline' : 'mdi-face-recognition';
    },
    capabilityWarnings() {
      if (!this.stream) return [];
      const visionWarning = this.visionReason
        || (['failed', 'unsupported'].includes(this.visionState) ? 'visionUnavailable' : null);
      const blurWarning = this.blurState === 'stopped' ? 'backgroundBlurStopped'
        : this.blurEnabled && this.blurState === 'unavailable'
          ? this.blurReason || 'backgroundBlurUnavailable'
          : null;
      const audioWarning = this.audioHint === 'microphoneAnalysisUnavailable' ? this.audioHint : null;
      return [...new Set([visionWarning, blurWarning, audioWarning].filter(Boolean))];
    },
    isBusy() {
      return this.sourceRequesting || Boolean(this.countdown) || this.isRecording || Boolean(this.mediaRecorder);
    },
    hasValidMaximumDuration() {
      return isValidEffectiveDuration(this.maximumDurationSeconds);
    },
    isSupported() {
      return this.capabilityState === 'supported';
    },
    displayElapsedSeconds() {
      return this.recordingStartedAt === null ? 0 : this.recordingElapsedSeconds;
    },
    displayRemainingSeconds() {
      return this.recordingStartedAt === null
        ? this.maximumDurationSeconds
        : this.recordingRemainingSeconds;
    },
    durationProgress() {
      if (!this.hasValidMaximumDuration || this.recordingStartedAt === null) {
        return 0;
      }

      return Math.min(100, Math.max(0, (this.recordingElapsedSeconds / this.maximumDurationSeconds) * 100));
    },
    durationProgressAriaText() {
      return this.$t('routes.guidedRecording.recordingTimeSummary', {
        elapsed: this.formatDuration(this.displayElapsedSeconds),
        remaining: this.formatDuration(this.displayRemainingSeconds, true),
      });
    },
    durationWarningMessage() {
      return this.$t('routes.guidedRecording.recordingFinalWarning', {
        seconds: this.durationWarningSeconds ?? Math.max(0, Math.ceil(this.recordingRemainingSeconds)),
      });
    },
  },
  watch: {
    sourceStream(value) { void this.bindSource(value); },
    blurEnabled(value) { this.runtime.pipeline?.setBlurEnabled(value); },
    isBusy: { immediate: true, handler(value) { this.$emit('busy-change', value); } },
    maximumDurationSeconds() {
      if (this.isRecording) {
        this.recordingInvalidated = true;
        this.recordingDurationInvalidReason = 'durationPolicyUnavailable';
        this.stopRecording();
        return;
      }

      this.resetRecordingTiming();
    },
  },
  mounted() {
    if (!isRecorderSupported()) {
      this.capabilityState = 'unsupported';
      return;
    }

    this.mimeType = getSupportedMimeType();
    if (!this.mimeType) {
      this.capabilityState = 'mimeUnsupported';
      return;
    }

    this.capabilityState = 'supported';
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    void this.bindSource(this.sourceStream);
  },
  beforeUnmount() {
    this.disposed = true;
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.clearCountdown();
    this.abortRecording();
    this.disposeSourcePipeline();
    this.$emit('busy-change', false);
  },
  methods: {
    resumeAudioAnalysis() {
      void this.runtime.analysis?.resume();
    },
    openPreview() {
      void this.runtime.analysis?.resume();
      this.errorKey = '';
      this.$emit('request-preview');
    },
    async bindSource(source) {
      this.clearCountdown();
      this.abortRecording();
      this.disposeSourcePipeline();
      this.errorKey = '';
      if (!source || this.disposed || !this.isSupported) return;
      const version = ++this.previewRequestVersion;
      this.stream = markRaw(source);
      this.blurState = this.blurEnabled ? 'warming' : 'off';
      source.getTracks().forEach((track) => {
        track.addEventListener('ended', this.handleStreamInterruption);
        this.runtime.tracks.push(track);
      });
      const video = markRaw(document.createElement('video'));
      video.muted = true;
      video.playsInline = true;
      video.srcObject = source;
      this.runtime.rawVideo = video;
      this.runtime.analysis = createGuidedAudioAnalysis({
        stream: source,
        onSample: ({ level, key }) => { this.audioLevel = level; this.audioHint = key; },
      });
      await this.$nextTick();
      if (this.disposed || version !== this.previewRequestVersion) return;
      if (typeof ResizeObserver === 'function') {
        this.runtime.previewObserver = new ResizeObserver(this.updatePreviewPlane);
        this.runtime.previewObserver.observe(this.$refs.livePreview);
      }
      this.syncPreviewStream(source);
      try { await video.play(); } catch {
        if (!this.disposed && version === this.previewRequestVersion) {
          this.errorKey = 'previewFailed';
          this.blurState = this.blurEnabled ? 'unavailable' : 'off';
        }
        return;
      }
      if (this.disposed || version !== this.previewRequestVersion) return;
      const pipeline = createGuidedVideoPipeline({
        sourceVideo: video,
        blurEnabled: this.blurEnabled,
        onGuidance: ({ face, framingState }) => {
          if (this.disposed || version !== this.previewRequestVersion) return;
          this.updateFraming(face, framingState);
        },
        onCapability: (state) => {
          if (this.disposed || version !== this.previewRequestVersion) return;
          this.blurState = state.blurState;
          this.visionState = state.visionState;
          this.visionReason = state.visionReason;
          this.blurReason = state.blurReason;
          if (state.visionState === 'failed' || state.visionState === 'unsupported') {
            this.updateFraming(null, 'unavailable');
          }
          this.$emit('capability-change', state);
          try {
            const preview = this.runtime.pipeline?.getRecordingStream(source.getAudioTracks()[0]);
            if (preview) this.syncPreviewStream(preview);
          } catch { this.syncPreviewStream(source); }
        },
      });
      this.runtime.pipeline = pipeline;
      await pipeline.start();
    },
    syncPreviewStream(stream) {
      const video = this.$refs.livePreview;
      if (!video || video.srcObject === stream) return;
      video.srcObject = stream;
      video.play().catch(() => { this.errorKey = 'previewFailed'; });
    },
    normalizedBoundsStyle(bounds) {
      return {
        left: `${bounds.x * 100}%`,
        top: `${bounds.y * 100}%`,
        width: `${bounds.width * 100}%`,
        height: `${bounds.height * 100}%`,
      };
    },
    updateFraming(face, state) {
      this.framingState = state;
      const tracked = state === 'tracking' && face?.bounds;
      this.faceBounds = tracked ? face.bounds : null;
      this.faceLandmarks = tracked
        ? [face.landmarks?.[33], face.landmarks?.[263], face.landmarks?.[1]].filter(Boolean)
        : [];
      this.framingCorrection = tracked ? getGuidedFramingCorrection(face) : null;
      if (tracked) this.hasTrackedFace = true;
    },
    updatePreviewPlane() {
      const video = this.$refs.livePreview;
      const rect = getGuidedPreviewRect(this.previewRatio, video?.clientWidth, video?.clientHeight);
      this.previewPlaneStyle = rect ? {
        left: `calc(50% + ${rect.x - video.clientWidth / 2}px)`,
        top: `calc(50% + ${rect.y - video.clientHeight / 2}px)`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
      } : null;
    },
    handleVisibilityChange() {
      if (document.hidden && this.countdown) this.clearCountdown();
      // Canvas RAF rendering pauses in hidden tabs. Preserve the captured portion
      // rather than silently recording frozen video alongside continuing audio.
      if (document.hidden && this.isRecording && this.recordingUsesCanvas) {
        this.recordingHiddenStop = true;
        this.stopRecording();
      }
    },
    updatePreviewDimensions() {
      const video = this.$refs.livePreview;
      if (video?.videoWidth > 0 && video?.videoHeight > 0) {
        this.previewAspectRatio = `${video.videoWidth} / ${video.videoHeight}`;
        this.previewRatio = video.videoWidth / video.videoHeight;
      }
      this.$nextTick(this.updatePreviewPlane);
    },
    disposeSourcePipeline() {
      this.previewRequestVersion += 1;
      this.runtime.previewObserver?.disconnect();
      this.runtime.previewObserver = null;
      this.runtime.tracks.forEach(track => track.removeEventListener('ended', this.handleStreamInterruption));
      this.runtime.tracks = [];
      this.runtime.pipeline?.dispose();
      this.runtime.pipeline = null;
      this.runtime.analysis?.dispose();
      this.runtime.analysis = null;
      if (this.runtime.rawVideo) {
        this.runtime.rawVideo.pause();
        this.runtime.rawVideo.srcObject = null;
      }
      this.runtime.rawVideo = null;
      if (this.$refs.livePreview) this.$refs.livePreview.srcObject = null;
      this.stream = null;
      this.audioLevel = 0;
      this.audioHint = null;
      this.updateFraming(null, 'loading');
      this.hasTrackedFace = false;
      this.visionState = 'loading';
      this.visionReason = null;
      this.blurReason = null;
      this.previewPlaneStyle = null;
      this.blurState = 'off';
    },
    focusDurationFeedback() {
      this.$nextTick(() => {
        const feedback = this.$refs.durationPolicyFeedback;
        const element = feedback?.$el || feedback;
        element?.focus?.();
      });
    },
    focusRecorderError() {
      this.$nextTick(() => {
        const feedback = this.$refs.errorFeedback;
        const element = feedback?.$el || feedback;
        element?.focus?.();
      });
    },
    getMonotonicTime() {
      const currentTime = readMonotonicTime();
      this.lastMonotonicTime = Math.max(this.lastMonotonicTime, currentTime);
      return this.lastMonotonicTime;
    },
    getMeasuredDurationSeconds(atTime = this.getMonotonicTime()) {
      if (!Number.isFinite(this.recordingStartedAt) || !Number.isFinite(atTime)) {
        return null;
      }

      return Math.max(0, (atTime - this.recordingStartedAt) / 1000);
    },
    formatDuration(durationSeconds, roundUp = false) {
      const safeDuration = Number.isFinite(durationSeconds) && durationSeconds >= 0
        ? durationSeconds
        : 0;
      const totalSeconds = roundUp ? Math.ceil(safeDuration) : Math.floor(safeDuration);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = String(totalSeconds % 60).padStart(2, '0');

      return `${minutes}:${seconds}`;
    },
    getDurationInvalidReason(durationSeconds, automaticStop) {
      if (!Number.isFinite(durationSeconds) || durationSeconds < 0) {
        return 'durationUnavailable';
      }
      if (this.isDurationValidForTake(durationSeconds, automaticStop)) {
        return '';
      }
      if (automaticStop
        && durationSeconds > this.maximumDurationSeconds + GUIDED_RECORDING_AUTOMATIC_STOP_TOLERANCE_SECONDS
      ) {
        return DURATION_TOLERANCE_INVALID_REASON;
      }
      if (durationSeconds > this.maximumDurationSeconds) {
        return DURATION_INVALID_REASON;
      }

      return '';
    },
    isDurationValidForTake(durationSeconds, automaticStop) {
      return isMeasuredDurationWithinLimit(
        durationSeconds,
        this.maximumDurationSeconds,
        automaticStop,
      );
    },
    resetRecordingTiming() {
      this.stopRecordingTimer();
      this.durationWarningAnnounced = false;
      this.durationWarningSeconds = null;
      this.recordingAutomaticStop = false;
      this.recordingDurationInvalidReason = '';
      this.recordingDurationValid = false;
      this.recordingElapsedSeconds = 0;
      this.recordingRemainingSeconds = 0;
      this.recordingStartedAt = null;
      this.recordingStopRequested = false;
      this.recordingStoppedAt = null;
    },
    setRecordingDuration(durationSeconds) {
      this.recordingElapsedSeconds = durationSeconds;
      this.recordingRemainingSeconds = Math.max(0, this.maximumDurationSeconds - durationSeconds);
    },
    startRecordingTimer() {
      this.stopRecordingTimer();
      this.recordingTimer = window.setInterval(() => {
        this.updateRecordingTimer();
      }, RECORDING_TIMER_INTERVAL_MS);
    },
    stopRecordingTimer() {
      if (this.recordingTimer !== null) {
        window.clearInterval(this.recordingTimer);
      }
      this.recordingTimer = null;
    },
    updateRecordingTimer() {
      if (!this.isRecording || this.recordingStopRequested) {
        return;
      }

      const measuredDurationSeconds = this.getMeasuredDurationSeconds();
      if (measuredDurationSeconds === null) {
        this.recordingInvalidated = true;
        this.recordingDurationInvalidReason = 'durationUnavailable';
        this.stopRecording({ automatic: true });
        return;
      }

      this.setRecordingDuration(measuredDurationSeconds);
      this.$emit('timer-tick', {
        elapsedSeconds: this.recordingElapsedSeconds,
        remainingSeconds: this.recordingRemainingSeconds,
      });
      if (!this.durationWarningAnnounced
        && this.recordingRemainingSeconds <= GUIDED_RECORDING_FINAL_WARNING_SECONDS
      ) {
        this.durationWarningAnnounced = true;
        this.durationWarningSeconds = Math.max(0, Math.ceil(this.recordingRemainingSeconds));
      }

      if (measuredDurationSeconds >= this.maximumDurationSeconds) {
        this.stopRecording({ automatic: true });
      }
    },
    startCountdown() {
      if (!this.hasValidMaximumDuration) {
        this.errorKey = 'durationPolicyUnavailable';
        this.focusDurationFeedback();
        return;
      }
      if (!this.stream || this.isBusy || this.previewOnly || document.hidden || this.blurState === 'warming') {
        return;
      }
      this.errorKey = '';
      // Resume in the initiating gesture; unavailable audio analysis never blocks capture.
      void this.runtime.analysis?.resume();
      const deadline = this.getMonotonicTime() + CAPTURE_PREROLL_SECONDS * 1000;
      this.countdown = CAPTURE_PREROLL_SECONDS;
      this.countdownTimer = window.setInterval(() => {
        if (document.hidden || !this.stream) { this.clearCountdown(); return; }
        const remaining = Math.max(0, Math.ceil((deadline - this.getMonotonicTime()) / 1000));
        if (remaining > 0) { this.countdown = remaining; return; }
        this.clearCountdown();
        this.startRecording();
      }, 100);
    },
    clearCountdown() {
      if (this.countdownTimer) {
        window.clearInterval(this.countdownTimer);
      }
      this.countdownTimer = null;
      this.countdown = 0;
    },
    startRecording() {
      if (!this.stream || !this.mimeType) {
        this.errorKey = 'mimeUnsupported';
        return;
      }
      if (!this.hasValidMaximumDuration) {
        this.errorKey = 'durationPolicyUnavailable';
        this.focusDurationFeedback();
        return;
      }
      this.recordedChunks = [];
      this.recordingInvalidated = false;
      this.recordingDurationInvalidReason = '';
      this.recordingDurationValid = false;
      this.recordingAutomaticStop = false;
      this.recordingStopRequested = false;
      this.recordingStartedAt = null;
      this.recordingStoppedAt = null;
      this.recordingElapsedSeconds = 0;
      this.recordingRemainingSeconds = this.maximumDurationSeconds;
      this.durationWarningAnnounced = false;
      this.durationWarningSeconds = null;
      try {
        const recordingStream = this.runtime.pipeline?.getRecordingStream(this.stream.getAudioTracks()[0]) || this.stream;
        this.recordingHiddenStop = false;
        this.recordingUsesCanvas = recordingStream.getVideoTracks()[0] !== this.stream.getVideoTracks()[0];
        this.mediaRecorder = markRaw(new window.MediaRecorder(recordingStream, { mimeType: this.mimeType }));
        this.mediaRecorder.ondataavailable = this.collectRecordedData;
        this.mediaRecorder.onstop = this.completeRecording;
        this.runtime.pipeline?.setRecording(true);
        this.mediaRecorder.start();
      } catch {
        this.mediaRecorder = null;
        this.runtime.pipeline?.setRecording(false);
        this.errorKey = 'previewFailed';
        return;
      }
      this.isRecording = true;
      this.$emit('is-recording', true);
      this.recordingStartedAt = this.getMonotonicTime();
      this.startRecordingTimer();
      this.updateRecordingTimer();
    },
    collectRecordedData(event) {
      if (event.data?.size) {
        this.recordedChunks.push(event.data);
      }
    },
    stopRecording({ automatic = false } = {}) {
      if (!this.isRecording && !this.mediaRecorder) {
        return;
      }
      if (this.recordingStopRequested) {
        return;
      }

      this.recordingStopRequested = true;
      this.recordingStoppedAt = this.getMonotonicTime();
      const measuredDurationSeconds = this.getMeasuredDurationSeconds(this.recordingStoppedAt);
      if (!this.recordingInvalidated) {
        this.recordingAutomaticStop = automatic;
        this.recordingDurationValid = this.isDurationValidForTake(
          measuredDurationSeconds,
          automatic,
        );
        this.recordingDurationInvalidReason = this.getDurationInvalidReason(
          measuredDurationSeconds,
          automatic,
        );
      }
      if (automatic
        && !this.recordingDurationValid
        && this.recordingDurationInvalidReason === DURATION_TOLERANCE_INVALID_REASON
      ) {
        this.recordingInvalidated = true;
      }
      if (measuredDurationSeconds !== null) {
        this.setRecordingDuration(measuredDurationSeconds);
      }
      this.stopRecordingTimer();
      if (this.mediaRecorder?.state === 'recording') {
        this.mediaRecorder.stop();
      } else {
        this.completeRecording();
      }
      this.isRecording = false;
      this.$emit('is-recording', false);
    },
    completeRecording() {
      const takeMimeType = this.mimeType;
      const takeAutomaticStop = this.recordingAutomaticStop;
      const reviewNoticeKey = this.recordingHiddenStop ? 'recordingStoppedHidden' : null;
      const takeMeasuredDurationSeconds = this.getMeasuredDurationSeconds(
        this.recordingStoppedAt ?? this.getMonotonicTime(),
      );
      const takeWasInvalidated = this.recordingInvalidated;
      const takeDurationIsValid = !takeWasInvalidated
        && this.isDurationValidForTake(takeMeasuredDurationSeconds, takeAutomaticStop);
      const takeInvalidReason = this.recordingDurationInvalidReason
        || (takeDurationIsValid ? '' : this.getDurationInvalidReason(
          takeMeasuredDurationSeconds,
          takeAutomaticStop,
        ));
      const blob = new Blob(this.recordedChunks, { type: this.mimeType });
      this.stopRecordingTimer();
      this.mediaRecorder = null;
      this.runtime.pipeline?.setRecording(false);
      this.isRecording = false;
      this.$emit('is-recording', false);
      this.recordedChunks = [];
      this.releaseStream();
      this.recordingInvalidated = false;
      if (takeWasInvalidated) {
        this.errorKey = 'invalidDuration';
        this.focusRecorderError();
        return;
      }
      if (!blob.size) {
        this.errorKey = 'emptyCapture';
        return;
      }
      this.$emit('recorded', {
        blob,
        mimeType: takeMimeType,
        measuredDurationSeconds: takeMeasuredDurationSeconds,
        automaticStop: takeAutomaticStop,
        durationValid: takeDurationIsValid,
        invalidReason: takeDurationIsValid ? null : takeInvalidReason || DURATION_INVALID_REASON,
        reviewNoticeKey,
      });
    },
    abortRecording() {
      if (this.mediaRecorder) {
        this.mediaRecorder.ondataavailable = null;
        this.mediaRecorder.onstop = null;
      }
      if (this.mediaRecorder?.state === 'recording') {
        this.mediaRecorder.stop();
      }
      this.mediaRecorder = null;
      this.runtime.pipeline?.setRecording(false);
      this.isRecording = false;
      this.$emit('is-recording', false);
      this.recordedChunks = [];
      this.recordingInvalidated = false;
      this.resetRecordingTiming();
    },
    handleStreamInterruption() {
      this.clearCountdown();
      this.abortRecording();
      this.releaseStream();
      this.errorKey = 'deviceDisconnected';
    },
    releaseStream() {
      this.clearCountdown();
      this.disposeSourcePipeline();
      this.stopRecordingTimer();
      this.$emit('release-source');
    },
  },
};
</script>

<style lang="scss" scoped>
@layer app {
  .guided-recorder { min-width: 0; }
  .guided-recorder__actions {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .guided-recorder__error:focus {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: 4px;
  }
  .guided-recorder__metrics {
    align-items: end;
    display: grid;
    grid-template-columns: minmax(0, 3fr) minmax(0, 1fr);
    gap: 12px;
  }
  .guided-recorder__duration { display: grid; gap: 4px; }
  .guided-recorder__duration-values {
    display: flex;
    flex-wrap: wrap;
    gap: 2px 12px;
    justify-content: space-between;
    font-size: 0.75rem;
  }
  .guided-recorder__preview {
    align-items: center;
    aspect-ratio: 16 / 9;
    background: #090b0e;
    border-radius: 8px;
    display: flex;
    justify-content: center;
    margin-inline: auto;
    overflow: hidden;
    position: relative;
    width: min(100%, calc(var(--guided-preview-max-height, min(50dvh, 480px)) * var(--guided-source-ratio, 1.7778)));
  }
  .guided-recorder__video { height: 100%; object-fit: contain; width: 100%; }
  .guided-recorder__preview-placeholder {
    color: rgba(255, 255, 255, 0.7);
    position: absolute;
  }
  .guided-recorder__framing-plane {
    pointer-events: none;
    position: absolute;
  }
  .guided-recorder__framing-target {
    border: 2px dashed rgba(255, 255, 255, 0.85);
    border-radius: 45%;
    position: absolute;
  }
  .guided-recorder__framing-target span {
    background: rgba(0, 0, 0, 0.8);
    border-radius: 4px;
    color: #fff;
    font-size: 0.65rem;
    left: 50%;
    padding: 2px 4px;
    position: absolute;
    top: 100%;
    transform: translateX(-50%);
    white-space: nowrap;
  }
  .guided-recorder__face-box {
    border: 2px solid #ffda75;
    border-radius: 8px;
    box-shadow: 0 0 0 1px #090b0e;
    position: absolute;
    transition: left 100ms linear, top 100ms linear, width 100ms linear, height 100ms linear;
  }
  .guided-recorder__face-box--good { border-color: #a7f3b5; border-width: 3px; }
  .guided-recorder__face-point {
    background: #fff;
    border: 1px solid #090b0e;
    border-radius: 50%;
    height: 5px;
    position: absolute;
    transform: translate(-50%, -50%);
    transition: left 100ms linear, top 100ms linear;
    width: 5px;
  }
  .guided-recorder__framing-cue {
    align-items: center;
    background: rgba(0, 0, 0, 0.8);
    border-radius: 6px;
    bottom: 8px;
    color: #fff;
    display: flex;
    font-size: 0.75rem;
    gap: 6px;
    max-width: calc(100% - 16px);
    padding: 4px 8px;
    pointer-events: none;
    position: absolute;
  }
  .guided-recorder__recording-indicator,
  .guided-recorder__countdown {
    background: rgba(0, 0, 0, 0.72);
    border-radius: 7px;
    color: #fff;
    font-weight: 700;
    padding: 6px 10px;
    position: absolute;
    z-index: 2;
  }
  .guided-recorder__recording-indicator {
    align-items: center;
    display: flex;
    gap: 4px;
    left: 12px;
    top: 12px;
  }
  .guided-recorder__countdown { font-size: 4rem; line-height: 1; }
  .guided-recorder__audio { display: grid; gap: 4px; min-width: 0; }
  .guided-recorder__audio-meter {
    background: rgb(var(--v-theme-tw-surface-accent));
    border-radius: 99px;
    height: 6px;
    overflow: hidden;
    width: 100%;
  }
  .guided-recorder__audio-meter span {
    background: rgb(var(--v-theme-primary));
    display: block;
    height: 100%;
    transition: width 160ms linear;
  }
  @media (max-width: 600px) {
    .guided-recorder__duration-values { flex-direction: column; }
  }
  @media (prefers-reduced-motion: reduce) {
    .guided-recorder__audio-meter span,
    .guided-recorder__face-box,
    .guided-recorder__face-point { transition: none; }
  }
}
</style>
